/**
 * Fetch Actions - Data fetching and cache management for snippets
 *
 * These actions handle data fetching and cache management.
 * They interact with the blockServiceClient to make API calls and update the store.
 *
 * Key behaviors:
 * - fetchSnippets: Implements request deduplication via isLoaded/isLoading flags
 * - clearCache: Resets the store to allow fresh data on next fetch
 *
 * For snippet creation with UI feedback, see createActions.ts
 */
import type { StoreActionApi } from 'react-sweet-state';

import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { clearSnippetsCache } from '../../services/cache/snippetsCache.ts';
import { reportError } from '../../utils/reportError.ts';
import type { Snippet } from '../../types/snippet.ts';
import { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';
import { invokePendingLoadCallbacks } from '../../utils/pendingCallbacks.ts';

export type FetchPerformanceMetrics = {
	snippetCount: number;
	userScopeCount: number;
	siteScopeCount: number;
	globalScopeCount: number;
	payloadSizeBytes: number;
	durationMs: number;
};

/**
 * Module-level callback for fetch performance metrics.
 * The host (React layer with analytics) can register a callback via setOnFetchComplete
 * to receive performance data after each successful fetch.
 */
let onFetchCompleteCallback: ((metrics: FetchPerformanceMetrics) => void) | null = null;

/**
 * Registers a callback that receives performance metrics after each successful fetch.
 * Call this from a React component/hook that has access to useAnalyticsEvents.
 */
export const setOnFetchComplete = (
	callback: ((metrics: FetchPerformanceMetrics) => void) | null,
): void => {
	onFetchCompleteCallback = callback;
};

export type FetchScopeErrorInfo = {
	scope: SnippetScope;
	statusCode?: number;
};

/**
 * Module-level callback for per-scope fetch errors.
 * The host (React layer with analytics) can register a callback via setOnFetchScopeError
 * to fire analytics events when an individual scope fetch fails.
 */
let onFetchScopeErrorCallback: ((info: FetchScopeErrorInfo) => void) | null = null;

/**
 * Registers a callback that fires when a per-scope snippet fetch fails.
 * Call this from a React component/hook that has access to useAnalyticsEvents.
 */
export const setOnFetchScopeError = (
	callback: ((info: FetchScopeErrorInfo) => void) | null,
): void => {
	onFetchScopeErrorCallback = callback;
};

/**
 * Invokes the registered per-scope fetch error callback, if any.
 * Called from blockServiceClient when a scope fetch fails.
 */
export const invokeFetchScopeErrorCallback = (info: FetchScopeErrorInfo): void => {
	onFetchScopeErrorCallback?.(info);
};

/**
 * Handles error reporting for fetch actions.
 * Sets fetchError state, reports to monitoring, and re-throws.
 */
const handleError = (
	error: unknown,
	setState: StoreActionApi<SnippetsState>['setState'],
	resetIsLoaded = true,
) => {
	const err = error instanceof Error ? error : new Error(String(error));
	setState({
		isLoading: false,
		fetchError: err,
		...(resetIsLoaded && { isLoaded: false }),
	});

	reportError(err);

	throw err;
};

/**
 * Actions for fetching and caching snippets data.
 */
export const fetchActions = {
	/**
	 * Fetches snippets from the server.
	 *
	 * Implements request deduplication: if snippets are already loaded (isLoaded=true)
	 * or a request is in progress (isLoading=true), returns cached data immediately.
	 * This allows multiple components to call fetchSnippets() without causing
	 * redundant API requests.
	 *
	 * Resolves site config (isSnippetSharingForSiteEnabled) and fetches user, global,
	 * and optionally site-scoped snippets; stores the config in state for later use.
	 *
	 * @returns Promise resolving to the snippets array
	 */
	fetchSnippets:
		(locale?: string) =>
		async ({ getState, setState }: StoreActionApi<SnippetsState>) => {
			const { isLoaded, isLoading, snippetsLocale } = getState();
			const isLoadedForRequestedLocale = isLoaded && snippetsLocale === locale;
			const isLoadingForRequestedLocale = isLoading && snippetsLocale === locale;

			// Request deduplication: return cached data if already loaded or loading for the requested locale
			if (isLoadedForRequestedLocale || isLoadingForRequestedLocale) {
				return getState().snippets;
			}

			setState({ isLoading: true, fetchError: null, snippetsLocale: locale });

			try {
				const fetchStart = performance.now();
				const isSnippetSharingForSiteEnabled =
					await BlockServiceClient.getIsSnippetSharingForSiteEnabled();
				const snippets = await BlockServiceClient.fetchSnippets({
					isSnippetSharingForSiteEnabled,
					locale,
				});
				const durationMs = Math.round(performance.now() - fetchStart);

				setState({
					snippets,
					isLoading: false,
					fetchError: null,
					isLoaded: true,
					isSnippetSharingForSiteEnabled,
					snippetsLocale: locale,
				});

				// Fire performance metrics callback if registered
				if (onFetchCompleteCallback) {
					const siteScopeCount = snippets.filter(
						(s: Snippet) => s.scope === SnippetScope.SITE,
					).length;
					const globalScopeCount = snippets.filter(
						(s: Snippet) => s.scope === SnippetScope.GLOBAL,
					).length;
					onFetchCompleteCallback({
						snippetCount: snippets.length,
						userScopeCount: snippets.length - globalScopeCount - siteScopeCount,
						siteScopeCount,
						globalScopeCount,
						payloadSizeBytes: new Blob([JSON.stringify(snippets)]).size,
						durationMs,
					});
				}

				// Invoke any one-time callbacks registered while this fetch was in progress
				invokePendingLoadCallbacks(snippets);

				return snippets;
			} catch (thrownError) {
				return handleError(thrownError, setState, true);
			}
		},

	/**
	 * Clears both the service cache and store state.
	 *
	 * Use this when you need to force a fresh fetch on the next request,
	 * such as after external changes to snippets data.
	 */
	clearCache:
		() =>
		({ setState }: StoreActionApi<SnippetsState>): void => {
			clearSnippetsCache();
			setState({
				snippets: [],
				isLoaded: false,
			});
		},
};
